/**
 * `slCache` document schema for the v2 section signup data model.
 *
 * One slCache doc exists per course at
 * `course/{courseId}/sectionSignupsV2/slCache`. It maps each
 * countable section leader's userId to their current slot bucket
 * and is the single source the pure aggregation helper uses to
 * count SLs per slot. Maintained reactively by the sections
 * onWrite trigger; see `updateSectionSignupV2SlCacheOnSectionWrite`
 * in `@cip/functions` for the write path.
 *
 * Holds at most a few hundred entries (one per active SL per
 * course), comfortably under the Firestore 1 MB document limit.
 *
 * **Last-writer-wins per userId.** If an SL somehow owns two
 * countable sections at the same time, the most recent section
 * trigger overwrites the entry. The dashboard surfaces drift if
 * this actually matters.
 */
import {z} from 'zod';

import {
  SECTION_SIGNUP_V2_TYPES,
  type SectionSignupV2Type,
} from './sectionSignupV2Paths';

/**
 * Zod schema for the {@link SectionSignupV2Type} string-enum
 * field. Lives here so other v2 schemas can reuse it.
 */
export const SectionSignupV2TypeSchema: z.ZodType<SectionSignupV2Type> = z.enum(
  SECTION_SIGNUP_V2_TYPES as readonly [
    SectionSignupV2Type,
    ...SectionSignupV2Type[],
  ],
);

/** One entry in {@link SectionSignupV2SlCache.entries}. */
export const SectionSignupV2SlCacheEntrySchema = z.object({
  type: SectionSignupV2TypeSchema,
  timeIndex: z.number().int(),
});

export type SectionSignupV2SlCacheEntry = z.infer<
  typeof SectionSignupV2SlCacheEntrySchema
>;

/**
 * The full `slCache` document. `entries` is keyed by section
 * leader userId; looking up `entries[sl]` tells you the slot the
 * SL is currently assigned to. A missing key means the SL is not
 * countable right now (no valid section, or inactive role).
 *
 * `.loose()` so IO-managed fields like `updatedAt` attached by a
 * converter pass through on read without appearing in the
 * inferred type.
 */
export const SectionSignupV2SlCacheSchema = z
  .object({
    courseId: z.string(),
    /** Section leader userId → current slot bucket. */
    entries: z.record(z.string(), SectionSignupV2SlCacheEntrySchema),
  })
  .loose();

export type SectionSignupV2SlCache = z.infer<
  typeof SectionSignupV2SlCacheSchema
>;
